import { lazy, Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import styles from "./App.module.css";
import AuthPage from "./components/AuthPage";
import ConfirmEmailForm from "./components/ConfirmEmailForm";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Footer from "./components/Footer";
import ImpersonationPanel from "./components/ImpersonationPanel";
import LoadingSpinner from "./components/LoadingSpinner";
// Eagerly loaded components (shared/essential)
import NavBar from "./components/NavBar";
import { OrderErrorBoundary } from "./components/orders/OrderErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";

// Lazy loaded components (route-specific)
const Dashboard = lazy(() => import("./components/Dashboard"));
const CustomerQuote = lazy(() => import("./components/pricing/CustomerQuote"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const CustomerDashboard = lazy(() => import("./components/CustomerDashboard"));
const CreateOrder = lazy(() => import("./components/CreateOrder"));
const EditProfile = lazy(() => import("./components/EditProfile"));
const OrderDetails = lazy(() => import("./components/orders/OrderDetails"));
const OrderEdit = lazy(() => import("./components/orders/OrderEdit"));
const TenantRegistration = lazy(
  () => import("./components/TenantRegistration"),
);
const TenantManagement = lazy(() => import("./components/TenantManagement"));
const TenantManagerDetails = lazy(
  () => import("./components/TenantManagerDetails"),
);
const CustomerInvites = lazy(() => import("./components/CustomerInvites"));
const TenantBranding = lazy(() => import("./components/TenantBranding"));
const CustomerListPage = lazy(() => import("./components/CustomerListPage"));
const CustomerManagement = lazy(
  () => import("./components/CustomerManagement"),
);
const TenantSettings = lazy(() => import("./components/TenantSettings"));
const CemeteryManagement = lazy(
  () => import("./components/CemeteryManagement"),
);
const ResetPasswordForm = lazy(() => import("./components/ResetPasswordForm"));

import NavigationPrefetch from "./components/NavigationPrefetch";
import { validateImpersonationState } from "./services/impersonationSlice";
import { RootState } from "./store";
import { hasRole } from "./utils/roleHelpers";

const App = () => {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);

  // validate impersonation state whenever token changes
  useEffect(() => {
    if (token) {
      dispatch(validateImpersonationState());
    }
  }, [dispatch, token]);

  // redirect old domains to new ones
  const hostname = window.location.hostname;
  if (hostname === "platform.foreverstoneengraving.com") {
    window.location.replace(
      window.location.href.replace(hostname, "monulink.com"),
    );
    return null;
  }
  if (hostname === "staging.foreverstoneengraving.com") {
    window.location.replace(
      window.location.href.replace(hostname, "staging.monulink.com"),
    );
    return null;
  }

  return (
    <ErrorBoundary>
      <Router>
        {token && <NavBar />}
        {token && <NavigationPrefetch />}
        <div className={styles.container}>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/login" element={<AuthPage />} />
              {/* redirect old /auth to /login */}
              <Route path="/auth" element={<Navigate to="/login" replace />} />
              <Route path="/reset-password" element={<ResetPasswordForm />} />
              <Route path="/confirm-email" element={<ConfirmEmailForm />} />
              <Route
                path="/register-business"
                element={<TenantRegistration />}
              />

              {/* developer routes */}
              <Route
                path="/developer/cemetery-management"
                element={
                  <ProtectedRoute>
                    {hasRole(user?.roles, "developer") ? (
                      <CemeteryManagement />
                    ) : (
                      <Navigate to="/tenant-manager/tenants" replace />
                    )}
                  </ProtectedRoute>
                }
              />
              {/* other developer routes - redirect to tenant-manager interface */}
              <Route
                path="/developer/*"
                element={<Navigate to="/tenant-manager" replace />}
              />

              {/* tenant-manager routes (accessible by both tenant-manager and developer roles) */}
              <Route
                path="/tenant-manager/*"
                element={
                  <ProtectedRoute>
                    {hasRole(user?.roles, "tenant-manager") ||
                    hasRole(user?.roles, "developer") ? (
                      <Dashboard />
                    ) : (
                      <Navigate to="/login" />
                    )}
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="tenants" />} />
                <Route path="dashboard" element={<Navigate to="tenants" />} />
                <Route path="tenants" element={<TenantManagement />} />
                <Route path="tenants/:id" element={<TenantManagerDetails />} />
              </Route>

              <Route
                path="/edit-profile"
                element={
                  <ProtectedRoute>
                    <EditProfile />
                  </ProtectedRoute>
                }
              />

              {/* public tenant-scoped login and registration routes */}
              <Route path="/:tenantSlug/login" element={<AuthPage />} />
              <Route path="/:tenantSlug/register" element={<AuthPage />} />
              <Route
                path="/:tenantSlug/reset-password"
                element={<AuthPage />}
              />

              {/* tenant-scoped routes */}
              <Route
                path="/:tenantSlug"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="orders" />} />
                {hasRole(user?.roles, "tenant-owner") && (
                  <Route path="orders" element={<AdminDashboard />} />
                )}
                {hasRole(user?.roles, "customer") && (
                  <Route path="orders" element={<CustomerDashboard />} />
                )}
                <Route path="get-quote" element={<CustomerQuote />} />
                <Route path="create-order" element={<CreateOrder />} />
                <Route
                  path="order/:id"
                  element={
                    <OrderErrorBoundary>
                      <OrderDetails
                        isAdmin={hasRole(user?.roles, "tenant-owner")}
                      />
                    </OrderErrorBoundary>
                  }
                />
                <Route
                  path="order/:id/edit"
                  element={
                    <OrderErrorBoundary>
                      <OrderEdit />
                    </OrderErrorBoundary>
                  }
                />
                <Route path="invites" element={<CustomerInvites />} />
                {hasRole(user?.roles, "tenant-owner") && (
                  <Route path="branding" element={<TenantBranding />} />
                )}
                {hasRole(user?.roles, "tenant-owner") && (
                  <Route path="customers" element={<CustomerListPage />} />
                )}
                {hasRole(user?.roles, "tenant-owner") && (
                  <Route
                    path="customer-management"
                    element={<CustomerManagement />}
                  />
                )}
                {hasRole(user?.roles, "tenant-owner") && (
                  <Route
                    path="cemetery-management"
                    element={<CemeteryManagement />}
                  />
                )}
                {(hasRole(user?.roles, "tenant-owner") ||
                  hasRole(user?.roles, "developer")) && (
                  <Route path="settings" element={<TenantSettings />} />
                )}
              </Route>

              {/* legacy order routes - redirect to tenant-scoped versions */}
              <Route
                path="/orders"
                element={
                  <ProtectedRoute>
                    <Navigate
                      to={
                        user?.tenantSlug
                          ? `/${user.tenantSlug}/orders`
                          : "/login"
                      }
                      replace
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order/*"
                element={
                  <ProtectedRoute>
                    {user?.tenantSlug ? (
                      <Navigate
                        to={`/${user.tenantSlug}/order/${window.location.pathname.split("/order/")[1] || ""}`}
                        replace
                      />
                    ) : (
                      <Navigate to="/login" replace />
                    )}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create-order"
                element={
                  <ProtectedRoute>
                    <Navigate
                      to={
                        user?.tenantSlug
                          ? `/${user.tenantSlug}/create-order`
                          : "/login"
                      }
                      replace
                    />
                  </ProtectedRoute>
                }
              />

              {/* root redirect based on user role */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Navigate
                      to={
                        hasRole(user?.roles, "developer") ||
                        hasRole(user?.roles, "tenant-manager")
                          ? "/tenant-manager/tenants"
                          : user?.tenantSlug
                            ? `/${user.tenantSlug}/orders`
                            : "/login"
                      }
                      replace
                    />
                  </ProtectedRoute>
                }
              />

              <Route
                path="*"
                element={<Navigate to={token ? "/" : "/login"} />}
              />
            </Routes>
          </Suspense>
        </div>
        {token && <Footer />}
        {token && <ImpersonationPanel />}
      </Router>
    </ErrorBoundary>
  );
};

export default App;
